import * as React from 'react'
import type { SalesReadinessDto, SalesSettingsDto } from '@lib/dto/sales-setup.dto'
import { milliToPercent, percentToMilli } from '@lib/helpers/sales-money'
import { putSalesSettingsInputSchema } from '@lib/input/sales-setup.input'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSalesReadiness, useSalesSettings, useSaveSalesSettings } from '@/hooks/useSales'

/** What the venue still needs before it can send a proposal, or that it's ready. */
function Readiness({ readiness }: { readiness: SalesReadinessDto }) {
  if (readiness.missing.length === 0) {
    return (
      <Callout tone="success" title="Ready to send proposals">
        {readiness.sendingOpen ? 'Send one from an inquiry’s page.' : 'Sending proposals opens soon.'}
      </Callout>
    )
  }
  return (
    <Callout tone="neutral" title="Before this venue can send a proposal">
      <ul className="m-0 list-disc space-y-0.5 pl-5">
        {readiness.missing.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </Callout>
  )
}

/** "1, 7" → [1, 7]; null when any part isn't a whole number of days. */
function daysOf(text: string): number[] | null {
  if (text.trim() === '') return []
  const parts = text.split(/[,\s]+/).filter((p) => p !== '')
  const days = parts.map((p) => Number.parseInt(p, 10))
  return days.every((d, i) => Number.isInteger(d) && String(d) === parts[i]) ? days : null
}

function Editor({ settings, venueId }: { settings: SalesSettingsDto; venueId: string }) {
  const { toast } = useToast()
  const save = useSaveSalesSettings(venueId)
  const [validDays, setValidDays] = React.useState(String(settings.validDays))
  const [lapse, setLapse] = React.useState(String(settings.lapseReminderDays))
  const [discount, setDiscount] = React.useState(milliToPercent(settings.bankDiscountMilli))
  const [beforeOn, setBeforeOn] = React.useState(settings.reminderBeforeDays !== null)
  const [before, setBefore] = React.useState(String(settings.reminderBeforeDays ?? 7))
  const [onDue, setOnDue] = React.useState(settings.reminderOnDue)
  const [after, setAfter] = React.useState(settings.reminderAfterDays.join(', '))
  const [error, setError] = React.useState<string | null>(null)

  function handleSave(): void {
    const bankDiscountMilli = percentToMilli(discount === '' ? '0' : discount)
    const reminderAfterDays = daysOf(after)
    if (bankDiscountMilli === null) return setError('Enter the bank-transfer discount as a percentage, like 2.')
    if (reminderAfterDays === null) return setError('Enter the late reminders as days, like 1, 7.')
    const parsed = putSalesSettingsInputSchema.safeParse({
      validDays: Number.parseInt(validDays, 10),
      lapseReminderDays: Number.parseInt(lapse === '' ? '0' : lapse, 10),
      bankDiscountMilli,
      reminderBeforeDays: beforeOn ? Number.parseInt(before, 10) : null,
      reminderOnDue: onDue,
      reminderAfterDays,
    })
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      const field = issue?.path[0]
      if (field === 'validDays') return setError('A proposal stays open for 1 to 90 days.')
      if (field === 'lapseReminderDays') return setError('The lapse reminder is 0 to 30 days before; 0 turns it off.')
      if (field === 'bankDiscountMilli') return setError('The bank-transfer discount can be up to 10%.')
      if (field === 'reminderBeforeDays') return setError('The reminder before a payment is due is 1 to 60 days.')
      return setError(issue?.code === 'custom' ? issue.message : 'Late reminders are up to three, each 1 to 60 days.')
    }
    setError(null)
    save.mutate(parsed.data, { onSuccess: () => toast('Sales settings saved.'), onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <fieldset disabled={!settings.canEdit} className="m-0 min-w-0 space-y-6 border-0 p-0">
      <section className="flex flex-wrap gap-4">
        <Field label="A proposal stays open for (days)" htmlFor="valid-days">
          <Input id="valid-days" inputMode="numeric" className="w-24 tabular-nums" value={validDays} onChange={(e) => setValidDays(e.target.value)} />
        </Field>
        <Field label="Remind the couple before it lapses (days)" htmlFor="lapse-days" hint="0 turns this off.">
          <Input id="lapse-days" inputMode="numeric" className="w-24 tabular-nums" value={lapse} onChange={(e) => setLapse(e.target.value)} />
        </Field>
        <Field label="Off payments by bank transfer (%)" htmlFor="bank-discount" hint="The couple sees both amounts. 0 for none.">
          <Input id="bank-discount" inputMode="decimal" className="w-24 tabular-nums" value={discount} onChange={(e) => setDiscount(e.target.value)} />
        </Field>
      </section>
      <section className="space-y-3">
        <h3 className="m-0 text-sm font-semibold text-ink">Payment reminders</h3>
        <p className="m-0 text-sm text-ink-muted">Emailed to the couple. A couple on autopay is always told three days before each charge.</p>
        <label className="flex flex-wrap items-center gap-2 text-sm text-ink">
          <Checkbox checked={beforeOn} onCheckedChange={setBeforeOn} />
          Before a payment is due,
          <Input
            size="sm"
            inputMode="numeric"
            aria-label="Days before a payment is due"
            className="w-16 tabular-nums"
            value={before}
            disabled={!beforeOn || !settings.canEdit}
            onChange={(e) => setBefore(e.target.value)}
          />
          days ahead
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <Checkbox checked={onDue} onCheckedChange={setOnDue} />
          On the day it’s due
        </label>
        <Field label="After it’s late (days, up to three)" htmlFor="after-days" hint="Like 1, 7. Leave empty for none.">
          <Input id="after-days" className="w-40 tabular-nums" value={after} onChange={(e) => setAfter(e.target.value)} />
        </Field>
      </section>
      {error === null ? null : <FormError>{error}</FormError>}
      {settings.canEdit ? (
        <FormActions>
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </FormActions>
      ) : null}
    </fieldset>
  )
}

/** A venue's Sales settings, and what it still needs before it can send a proposal (docs/sales.spec.md). */
export function SalesSettingsSection({ venueId }: { venueId: string }) {
  const settings = useSalesSettings(venueId)
  const readiness = useSalesReadiness(venueId)
  if (settings.isPending) return <DetailSkeleton />
  if (settings.isError) return <ErrorState message={requestErrorMessage(settings.error)} onRetry={() => void settings.refetch()} />
  return (
    <div className="space-y-6">
      {readiness.data === undefined ? null : <Readiness readiness={readiness.data} />}
      <Editor key={settings.dataUpdatedAt} settings={settings.data} venueId={venueId} />
    </div>
  )
}
